import { Box, Paper, styled, Tab } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAtom, useAtomValue } from 'jotai';
import { TabContext, TabList } from '@mui/lab';
import { ConfirmDialog } from '../../../../../../components/Dialog/ConfirmDialog/ConfirmDialog';
import { FooterButtons } from '../../../../../../components/Dialog/ConfirmDialog/FooterButtons';
import {
  kpisSelectedTemplateAssignmentStateJ,
  kpiTemplateAssignmentsByIdStateJ,
} from '../../../../../../services/state/KPI/KPICompaniesStateJ';
import { portcosByIdMapAtomJ } from '../../../../../../services/state/PortCosStateJ';
import { IKPITemplate } from '../../../../../../data-models/kpi-template.data-model';
import { IKPIRequestForm, KPIRequestFrequency } from '../../../../../../data-models/kpi-requests.data-model';
import { CustomTabPanel } from '../../../KPIRequests/KPIRequestsTabs';
import { Form } from '../../../../../../components/Form/FormComponents';
import { useKPITemplateAssignments } from '../../hooks/useKPITemplateAssigmentsActions';
import { kpisAssignRequestModalState } from '../../../../../../services/state/KPI/KPIRequestsStateJ';
import { kpiActiveTemplatesListStateJ } from '../../../../../../services/state/KPI/KPITemplatesStateJ';
import { useLoadingBarState } from '../../../../../../components/LoadingBar/LoadingBarContext';
import { DefaultReminderThreshold } from '../../../../../../data-models/template.data-model';
import { KPIAssignRequestModalSettings } from './KPIAssignRequestModalSettings';
import { KPIAssignRequestModalSetTemplate } from './KPIAssignRequestModalSetTemplate';

export const TemplatePaperContainer = styled(Paper)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0;
`;

const CustomTabLeftText = styled(Tab)`
  padding: 0;
  align-items: flex-start;
`;

export const GoExternalLinkIconWrapper = styled('div')`
  position: relative;
  right: 50px;
  top: -20px;
`;

const defaultValues = {
  templateUuid: '',
  frequency: KPIRequestFrequency.Monthly,
  // fye is december as default values because it's most common
  reminderThreshold: DefaultReminderThreshold,
  fye: '12',
  respondent: [],
};

/**@deprecated */
export function KPIAssignRequestModal() {
  const [isOpenAssignRequestModal, setIsOpenAssignRequestModal] = useAtom(kpisAssignRequestModalState);
  const [selectedTemplateAssignationId, setSelectedTemplateAssignationId] = useAtom(
    kpisSelectedTemplateAssignmentStateJ
  );
  const templatesList = useAtomValue(kpiActiveTemplatesListStateJ);
  const kpisTemplatesAssignmentsMapById = useAtomValue(kpiTemplateAssignmentsByIdStateJ);
  const { createTemplateAssignmentRequest, updateTemplateAssignmentRequest } = useKPITemplateAssignments();
  const [stepTab, setStepTab] = useState('set template');
  const portCompaniesById = useAtomValue(portcosByIdMapAtomJ);
  const {
    state: { isLoading },
  } = useLoadingBarState();

  const methods = useForm<Partial<IKPIRequestForm>>({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    if (selectedTemplateAssignationId === -1) {
      return;
    }

    const existingTemplateAssigment = kpisTemplatesAssignmentsMapById.get(selectedTemplateAssignationId);

    if (!existingTemplateAssigment) {
      return;
    }

    methods.reset({
      templateUuid: existingTemplateAssigment?.templateUuid,
      frequency: existingTemplateAssigment?.frequency,
      fye: methods.formState.defaultValues?.fye,
      respondent: existingTemplateAssigment?.respondent,
      reminderThreshold: existingTemplateAssigment.reminderThreshold ?? DefaultReminderThreshold,
    });
  }, [kpisTemplatesAssignmentsMapById, methods, selectedTemplateAssignationId]);

  const selectedCompany = useMemo(() => {
    if (!portCompaniesById || isOpenAssignRequestModal === -1) {
      return;
    }

    return portCompaniesById.get(isOpenAssignRequestModal);
  }, [isOpenAssignRequestModal, portCompaniesById]);

  const selectedTemplatedId = methods.watch('templateUuid');

  const selectedTemplate = useMemo(() => {
    if (!selectedTemplatedId) {
      return;
    }
    if (!templatesList) {
      return;
    }

    return templatesList?.find((template: IKPITemplate) => template.uuid === selectedTemplatedId);
  }, [selectedTemplatedId, templatesList]);

  const onCloseModalAndReset = useCallback(() => {
    setSelectedTemplateAssignationId(-1);
    setStepTab('set template');
    methods.reset(defaultValues);
    setIsOpenAssignRequestModal(-1);
  }, [setIsOpenAssignRequestModal, setSelectedTemplateAssignationId, methods]);

  const onSubmit = useCallback(
    async (data: Partial<IKPIRequestForm>) => {
      if (!data.respondent) {
        methods.setError('respondent', {
          type: 'manual',
          message: 'Respondent is required',
        });
      }

      methods.clearErrors('respondent');

      const payload = {
        data: {
          ...data,
          fye: Number(data.fye),
          companyId: isOpenAssignRequestModal,
        },
        templateName: selectedTemplate?.name,
        companyName: selectedCompany?.name,
      };
      // if there was no selected assignation, it means we are creating a new one
      if (selectedTemplateAssignationId === -1) {
        await createTemplateAssignmentRequest({
          ...payload,
        });
      } else {
        await updateTemplateAssignmentRequest({
          ...payload,
          data: {
            ...payload.data,
            id: selectedTemplateAssignationId,
          },
        });
      }

      onCloseModalAndReset();
    },
    [
      createTemplateAssignmentRequest,
      isOpenAssignRequestModal,
      methods,
      onCloseModalAndReset,
      selectedCompany?.name,
      selectedTemplate?.name,
      selectedTemplateAssignationId,
      updateTemplateAssignmentRequest,
    ]
  );

  const handleNavTab = useCallback(() => {
    if (stepTab === 'set template') {
      return setStepTab('settings');
    }
    setStepTab('set template');
  }, [stepTab]);

  const onChangeTab = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      if (!selectedTemplatedId) {
        return;
      }
      setStepTab(newValue);
    },
    [selectedTemplatedId]
  );

  const actionButtons = useMemo(() => {
    if (stepTab === 'set template') {
      return (
        <FooterButtons
          confirmButtonText='Next'
          onConfirmDisabled={!selectedTemplatedId}
          onCancel={onCloseModalAndReset}
          onConfirm={handleNavTab}
        />
      );
    }

    const submitButtonText = selectedTemplateAssignationId !== -1 ? 'Update' : 'Assign';

    return (
      <FooterButtons
        confirmButtonText={submitButtonText}
        loading={isLoading}
        onCancel={onCloseModalAndReset}
        onConfirm={methods.handleSubmit(onSubmit)}
      />
    );
  }, [
    stepTab,
    selectedTemplateAssignationId,
    isLoading,
    onCloseModalAndReset,
    methods,
    onSubmit,
    selectedTemplatedId,
    handleNavTab,
  ]);

  const modalTitle = useMemo(() => {
    if (selectedTemplateAssignationId !== -1) {
      return 'Edit';
    }
    return 'Assign';
  }, [selectedTemplateAssignationId]);

  return (
    <ConfirmDialog
      open={isOpenAssignRequestModal !== -1}
      title={`${modalTitle} Request To ${selectedCompany?.name ?? ''}`}
      onClose={onCloseModalAndReset}
      onConfirm={() => setIsOpenAssignRequestModal(-1)}
      actionButtons={actionButtons}
    >
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <TabContext value={stepTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList variant='fullWidth' aria-label='tab list' onChange={onChangeTab}>
                <CustomTabLeftText label={'1. Set Template'} value='set template' />
                <CustomTabLeftText disabled={!selectedTemplatedId} label='2. Settings' value='settings' />
              </TabList>
            </Box>
            <CustomTabPanel value='set template'>
              <KPIAssignRequestModalSetTemplate />
            </CustomTabPanel>
            <CustomTabPanel value='settings'>
              <KPIAssignRequestModalSettings />
            </CustomTabPanel>
          </TabContext>
        </Form>
      </FormProvider>
    </ConfirmDialog>
  );
}
